import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import dayjsBusinessDays from 'dayjs-business-days2';
import moment from 'moment';
import numeral from 'numeral';

import type {FormattedValue} from 'nutshell-core/types';

dayjs.extend(weekday);
dayjs.extend(dayjsBusinessDays);

type QuotaModelJsonActivity = {
    quotaType: 'activities';
    currentValue: {
        count: number;
        startTime: number;
        endTime: number;
    };
    quotaValue: {
        count: number;
        startTime: number;
        endTime: number;
    };
};

type QuotaModelJsonSales = {
    quotaType: 'sales';
    currentValue: {
        amount: FormattedValue;
        startTime: number;
        endTime: number;
    };
    quotaValue: {
        amount: FormattedValue;
        startTime: number;
        endTime: number;
    };
};

type QuotaModelJson = QuotaModelJsonActivity | QuotaModelJsonSales;

/**
 * Helper function to run some null-checks on our modelJson to ensure we're
 * not trying to access undefined start and endTimes when getting the current
 * percent to end time from the quotaValue
 * @param  {QuotaModelJson} modelJson - Full quotaModelJson object
 * @param  {number} unixSecsNow   - The number of unix seconds to consider "current time"
 *                                  (defaults to current time - moment().unix())
 * @param  {Object} businessHours - The settings of start and end times, along with a format to parse them with
 * @return {number}                   - Fraction of period elapsed (between 0 & 1)
 */
export function calculateCurrentPercentToEndTimeFromJson(
    modelJson: QuotaModelJson,
    unixSecsNow: number = moment().unix(),
    businessHours: {
        end: string;
        start: string;
        format: string;
    } = NutshellFrontendSettings.businessHours
) {
    if (!modelJson || !modelJson.quotaValue) return undefined;

    return calculateCurrentPercentToEndTime(
        modelJson.quotaValue.startTime,
        modelJson.quotaValue.endTime,
        unixSecsNow,
        businessHours
    );
}

/**
 * Find the fraction of total period that has elapsed.
 *
 * @param  {number} startTime     - Unix timestamp of when the quota period starts
 * @param  {number} endTime       - Unix timestamp of when the quota period ends
 * @param  {number} unixSecsNow   - The number of unix seconds to consider "current time"
 *                                  (defaults to current time - moment().unix())
 * @param  {Object} businessHours - The settings of start and end times, along with a format to parse them with
 * @return {number}               - Fraction of period elapsed (between 0 & 1)
 */
export function calculateCurrentPercentToEndTime(
    startTime?: number,
    endTime?: number,
    unixSecsNow: number = moment().unix(),
    businessHours: {
        end: string;
        start: string;
        format: string;
    } = NutshellFrontendSettings.businessHours
) {
    // dayjs-business-days2 uses Monday-Friday as default business days

    if (!startTime || !endTime) return undefined;

    // Need to find the start of business hours on the day of unixSecsNow
    const currentTime = moment.unix(unixSecsNow);
    const dateFormat = 'YYYY-MM-DD'; // Going to set the date to the current time, this helps with DST changes
    const businessHoursStart = moment(
        `${businessHours.start} ${currentTime.format(dateFormat)}`,
        `${businessHours.format} ${dateFormat}`
    );
    const businessHoursEnd = moment(
        `${businessHours.end} ${currentTime.format(dateFormat)}`,
        `${businessHours.format} ${dateFormat}`
    );
    const businessHoursPerDay = businessHoursEnd.diff(businessHoursStart, 'hours', true);

    const currentDayBusinessHoursStart = currentTime.clone().startOf('day').add({
        hours: businessHoursStart.hour(),
        minutes: businessHoursStart.minute(),
        seconds: businessHoursStart.second(),
    });

    /*
     * Using the startTime and endTime, find the closest start and end of business hours.
     * This is because startTime is usually a Sunday morning and we don't care
     * about the hours from Sunday morning to Monday at start of business.
     * Likewise, we don't care about the hours from Friday close of business to Saturday night.
     */
    const workingTimeStart = moment.unix(roundToNextWorkday(startTime)).startOf('day').add({
        hours: businessHoursStart.hour(),
        minutes: businessHoursStart.minute(),
        seconds: businessHoursStart.second(),
    });
    const workingTimeEnd = moment.unix(roundToPreviousWorkday(endTime)).startOf('day').add({
        hours: businessHoursEnd.hour(),
        minutes: businessHoursEnd.minute(),
        seconds: businessHoursEnd.second(),
    });

    // Check that the currentTime is within the range of quotaValue that we care about
    if (currentTime.isBefore(workingTimeStart)) return 0;
    if (currentTime.isAfter(workingTimeEnd)) return 1;

    // Need to find the total number of working hours from startTime to endTime
    // @ts-expect-error
    const workingDaysTotal = dayjs(workingTimeEnd).businessDiff(dayjs(workingTimeStart));
    const workingTimeHoursTotal = workingDaysTotal * businessHoursPerDay;

    // Find the number of working hours that have completed before currentTime
    // businessDiff returns the number of business days between two dates (inclusive)
    // If we're before business hours on the current day, we shouldn't count any hours for today
    // If we're during/after business hours, we count the partial hours for today
    // @ts-expect-error
    const businessDaysFromStart = dayjs(currentTime).businessDiff(dayjs(workingTimeStart));

    const isAfterStartOfBusinessHours = currentTime.isAfter(currentDayBusinessHoursStart);

    // If during/after business hours, count complete days (businessDaysFromStart - 1) + partial day
    let workingDaysElapsed = Math.max(0, businessDaysFromStart);
    if (isAfterStartOfBusinessHours) {
        workingDaysElapsed = Math.max(0, businessDaysFromStart - 1);
    }

    const workingHoursElapsedToday = Math.max(
        0,
        Math.min(currentTime.diff(currentDayBusinessHoursStart, 'hours', true), businessHoursPerDay)
    );

    const workingTimeHoursElapsed =
        workingDaysElapsed * businessHoursPerDay + workingHoursElapsedToday;

    return workingTimeHoursElapsed / workingTimeHoursTotal;
}

function roundToNextWorkday(rawTimeInSecs: number) {
    // Assumes working days are Monday-Friday
    const inputDate = moment.unix(rawTimeInSecs);
    const inputDayOfWeek = inputDate.day();
    switch (inputDayOfWeek) {
        case 0:
            // Sunday
            inputDate.add(1, 'days');

            return inputDate.unix();
        case 6:
            // Saturday
            inputDate.add(2, 'days');

            return inputDate.unix();
        default:
            return rawTimeInSecs;
    }
}

function roundToPreviousWorkday(rawTimeInSecs: number) {
    // Assumes working days are Monday-Friday
    const inputDate = moment.unix(rawTimeInSecs);
    const inputDayOfWeek = inputDate.day();
    switch (inputDayOfWeek) {
        case 0:
            // Sunday
            inputDate.subtract(2, 'days');

            return inputDate.unix();
        case 6:
            // Saturday
            inputDate.subtract(1, 'days');

            return inputDate.unix();
        default:
            return rawTimeInSecs;
    }
}

/**
 * Determine whether the quota object has an assigned activity quota
 *
 * @param  {object}  modelJson - A single activity quota object
 * @return {Boolean}           - True if there is a current quota number in the object
 */
export function hasActivityQuota(modelJson: QuotaModelJson) {
    return Boolean(
        modelJson.quotaType === 'activities' &&
            modelJson.quotaValue &&
            modelJson.quotaValue.count > 0
    );
}

/**
 * Determine whether the quota object has an assigned sales quota
 *
 * @param  {object}  modelJson - A single activity quota object
 * @return {Boolean}           - True if there is a current quota number in the object
 */
export function hasSalesQuota(modelJson: QuotaModelJson) {
    return Boolean(
        modelJson.quotaType === 'sales' &&
            modelJson.quotaValue &&
            modelJson.quotaValue.amount &&
            modelJson.quotaValue.amount.value > 0
    );
}

/**
 * Determine whether a quota object actually has an assigned quota (either sales or activity)
 *
 * @param  {object}  modelJson - A single quota object
 * @return {Boolean}           - True if there is a current quota number in the object
 */
export function hasQuota(modelJson: QuotaModelJson) {
    return hasActivityQuota(modelJson) || hasSalesQuota(modelJson);
}

/**
 * Calculate how much of the quota has been reached so far.
 *
 * Note: the return value can be greater than zero if quota is exceeded.
 *
 * @param  {object}  modelJson - A single activityType object
 * @return {number}            - Fraction of quota completed so far
 */
export function calculatePercentComplete(modelJson: QuotaModelJson) {
    if (hasActivityQuota(modelJson) && modelJson.quotaType === 'activities') {
        return Number(modelJson.currentValue.count) / Number(modelJson.quotaValue.count);
    }

    if (hasSalesQuota(modelJson) && modelJson.quotaType === 'sales') {
        return Number(modelJson.currentValue.amount.value) / modelJson.quotaValue.amount.value;
    }
}

/**
 * Determine whether activities are ahead or behind schedule to meet quota.
 *
 * i.e. what is the difference between how many activities should have been completed
 * and the number actually completed?
 *
 * @param  {object}  modelJson           - A single quota object
 * @param  {number} percentTimeElapsed   - Fraction of quota period that has elapsed so far
 * @return {number}                      - Integer number ahead or behind schedule (positive if ahead)
 */
export function calculateDelta(modelJson: QuotaModelJson, percentTimeElapsed: number): number {
    if (hasActivityQuota(modelJson) && modelJson.quotaType === 'activities') {
        const quotaValue = Number(modelJson.quotaValue.count);
        const isVal = Number(modelJson.currentValue.count);
        const shouldBeVal = Math.round(percentTimeElapsed * quotaValue);

        return isVal - shouldBeVal;
    }

    if (hasSalesQuota(modelJson) && modelJson.quotaType === 'sales') {
        const quotaValue = modelJson.quotaValue.amount.value;
        const isVal = modelJson.currentValue.amount.value;
        const shouldBeVal = Math.round(percentTimeElapsed * quotaValue);

        return isVal - shouldBeVal;
    }

    return 0;
}

export function generateTooltipText(modelJson: QuotaModelJson, percentTimeElapsed: number) {
    let formattedDelta;
    const deltaNumber = calculateDelta(modelJson, percentTimeElapsed);
    if (hasSalesQuota(modelJson) && modelJson.quotaType === 'sales') {
        const {value, suffix} = formatCurrency({value: Math.abs(deltaNumber)});
        formattedDelta = `${modelJson.quotaValue.amount.prefix}${value}`;
        if (suffix) formattedDelta += suffix;
    }
    const delta = formattedDelta ? formattedDelta : Math.abs(deltaNumber);
    const percentToEndTimeRounded = Math.round(percentTimeElapsed * 100);
    let deltaStatus;
    if (deltaNumber < 0) {
        deltaStatus = formattedDelta
            ? `To stay on track, win at least ${delta} more`
            : `To stay on track, complete at least ${delta} more`;
    } else if (deltaNumber === 0) {
        deltaStatus = 'On track to meet the quota';
    } else {
        deltaStatus = `${delta} ahead of schedule`;
    }
    const timeStatus = `(${percentToEndTimeRounded}% of quota time has elapsed)`;

    return `${deltaStatus}\n${timeStatus}`;
}

export function formatCurrency(
    currency: {
        value: number;
    },
    currencyToSubtract: {
        value: number;
    } = {value: 0}
) {
    let currencyValue, currencySuffix;
    const currencyFormat = '00.[0]a';
    const formattedCurrency = numeral(currency.value - currencyToSubtract.value).format(
        currencyFormat
    );
    const match = formattedCurrency.match(/(.*)([A-Za-z])/);
    if (match) {
        // @ts-expect-error
        [, currencyValue, currencySuffix] = formattedCurrency.match(/(.*)([A-Za-z])/);
    } else {
        currencyValue = formattedCurrency;
    }

    return {
        value: currencyValue,
        suffix: currencySuffix,
    };
}
